export const TOPUP_BALANCE_PENDING_SESSION_KEY = 'ERA_TOPUP_BALANCE_PENDING'
export const TOPUP_BALANCE_PENDING_UPDATED_EVENT = 'ERA_TOPUP_BALANCE_PENDING_UPDATED'
export const TOPUP_BALANCE_CHANGED_EVENT = 'ERA_TOPUP_BALANCE_CHANGED'
export const TOPUP_BALANCE_PENDING_COOKIE_KEY = 'ERA_TOPUP_PENDING'
const TOPUP_BALANCE_PENDING_COOKIE_MAX_AGE_SECONDS = 180

export type T_TopupBalancePendingBaseline = {
  accountId: number
  currencyId: number | null
  amountValue: number
  blockedValue: number
  capturedAt: number
}

export type T_TopupBalancePendingSession = {
  startedAt: number
  requestId: number | null
  sessionKey: string
  baseline?: T_TopupBalancePendingBaseline
}

const normalizeStartedAt = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const normalizeRequestId = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const normalizeMoneyValue = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

const normalizeSessionKey = (value: unknown) => {
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  return normalized || null
}

const normalizeTopupBalancePendingBaseline = (
  value: unknown
): T_TopupBalancePendingBaseline | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const parsed = value as Partial<T_TopupBalancePendingBaseline>
  const accountId = normalizeRequestId(parsed.accountId)
  const amountValue = normalizeMoneyValue(parsed.amountValue)
  const blockedValue = normalizeMoneyValue(parsed.blockedValue)

  if (!accountId || amountValue === null || blockedValue === null) {
    return null
  }

  return {
    accountId,
    currencyId: normalizeRequestId(parsed.currencyId),
    amountValue,
    blockedValue,
    capturedAt: normalizeStartedAt(parsed.capturedAt) || Date.now(),
  }
}

const buildSessionKey = () => (
  `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
)

const setTopupPendingCookie = () => {
  if (typeof document === 'undefined') return false
  try {
    document.cookie = [
      `${TOPUP_BALANCE_PENDING_COOKIE_KEY}=1`,
      `Max-Age=${TOPUP_BALANCE_PENDING_COOKIE_MAX_AGE_SECONDS}`,
      'Path=/',
      'SameSite=Lax',
    ].join('; ')
    return true
  } catch {
    return false
  }
}

const clearTopupPendingCookie = () => {
  if (typeof document === 'undefined') return false
  try {
    document.cookie = [
      `${TOPUP_BALANCE_PENDING_COOKIE_KEY}=`,
      'Max-Age=0',
      'Path=/',
      'SameSite=Lax',
    ].join('; ')
    return true
  } catch {
    return false
  }
}

export const hasTopupBalancePendingCookie = () => {
  if (typeof document === 'undefined') return false
  try {
    return document.cookie
      .split(';')
      .some((chunk) => chunk.trim() === `${TOPUP_BALANCE_PENDING_COOKIE_KEY}=1`)
  } catch {
    return false
  }
}

export const readTopupBalancePendingSession = (): T_TopupBalancePendingSession | null => {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.sessionStorage.getItem(TOPUP_BALANCE_PENDING_SESSION_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<T_TopupBalancePendingSession>
    const startedAt = normalizeStartedAt(parsed.startedAt)

    if (!startedAt) return null

    const sessionKey = normalizeSessionKey(parsed.sessionKey) || `legacy_${startedAt}`

    return {
      startedAt,
      requestId: normalizeRequestId(parsed.requestId),
      sessionKey,
      baseline: normalizeTopupBalancePendingBaseline(parsed.baseline) || undefined,
    }
  } catch {
    return null
  }
}

export const setTopupBalancePendingSession = (payload?: {
  requestId?: number | null
  startedAt?: number
  sessionKey?: string
  baseline?: T_TopupBalancePendingBaseline | null
}) => {
  if (typeof window === 'undefined') return false

  const data: T_TopupBalancePendingSession = {
    startedAt: normalizeStartedAt(payload?.startedAt) || Date.now(),
    requestId: normalizeRequestId(payload?.requestId),
    sessionKey: normalizeSessionKey(payload?.sessionKey) || buildSessionKey(),
    baseline: normalizeTopupBalancePendingBaseline(payload?.baseline) || undefined,
  }

  let isStorageSaved = false
  try {
    window.sessionStorage.setItem(
      TOPUP_BALANCE_PENDING_SESSION_KEY,
      JSON.stringify(data),
    )
    isStorageSaved = true
  } catch {
    isStorageSaved = false
  }

  const isCookieSaved = setTopupPendingCookie()
  const isSaved = isStorageSaved || isCookieSaved

  window.dispatchEvent(new CustomEvent(
    TOPUP_BALANCE_PENDING_UPDATED_EVENT,
    { detail: { action: 'set', session: data } }
  ))

  return isSaved
}

export const clearTopupBalancePendingSession = () => {
  if (typeof window === 'undefined') return false

  let isStorageCleared = false
  try {
    window.sessionStorage.removeItem(TOPUP_BALANCE_PENDING_SESSION_KEY)
    isStorageCleared = true
  } catch {
    isStorageCleared = false
  }

  const isCookieCleared = clearTopupPendingCookie()
  const isCleared = isStorageCleared || isCookieCleared

  window.dispatchEvent(new CustomEvent(
    TOPUP_BALANCE_PENDING_UPDATED_EVENT,
    { detail: { action: 'clear' } }
  ))

  return isCleared
}
